import { FC, KeyboardEvent } from "react";
import { Badge } from "@app/shared/Badge";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { formatDateTime } from "@app/shared/formatDateTime";
import { SubmissionCommentAuthorRole, SubmissionCommentDisplay } from "@app/compliance-connect/network-submission/interfaces";

const roleLabels: Record<SubmissionCommentAuthorRole, string> = {
  [SubmissionCommentAuthorRole.Issuer]: "Issuer",
  [SubmissionCommentAuthorRole.Regulator]: "Regulator",
};

interface NetworkProfileCommentEntryProps {
  comment: SubmissionCommentDisplay;
  onView?: (submissionCommentId: number) => void;
}

export const NetworkProfileCommentEntry: FC<NetworkProfileCommentEntryProps> = ({ comment, onView }) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onView?.(comment.submissionCommentId);
    }
  };

  return (
    <div
      className="compliance-connect-network-profile__comment-entry"
      data-testid={`network-profile-comment-entry-${comment.submissionCommentId}`}
      role={onView ? "button" : undefined}
      tabIndex={onView ? 0 : undefined}
      onClick={onView ? () => onView(comment.submissionCommentId) : undefined}
      onKeyDown={onView ? handleKeyDown : undefined}
    >
      <div className="compliance-connect-network-profile__comment-avatar-column" aria-hidden="true">
        <div className="compliance-connect-network-profile__comment-avatar">
          <QesIcon iconName={QesIcons.UserV1} isDecorative width="16" />
        </div>
        <span className="compliance-connect-network-profile__comment-avatar-line" />
      </div>
      <div className="compliance-connect-network-profile__comment-body">
        <div className="compliance-connect-network-profile__comment-header">
          <div className="compliance-connect-network-profile__comment-header-top">
            <span className="compliance-connect-network-profile__comment-author">{comment.authorName ?? "—"}</span>
            <Badge badgeType="badge--neutral" className="compliance-connect-network-profile__comment-role-tag">
              {roleLabels[comment.authorRole]} · {comment.organizationName ?? "—"}
            </Badge>
            {!!onView && (
              <span
                className="compliance-connect-network-profile__comment-unread-dot"
                data-testid={`network-profile-comment-entry-${comment.submissionCommentId}-unread-dot`}
                aria-label="Unread comment"
              />
            )}
          </div>
          <span className="compliance-connect-network-profile__comment-timestamp">{formatDateTime(comment.dateCreated)}</span>
        </div>
        <p className="compliance-connect-network-profile__comment-text">{comment.commentText}</p>
      </div>
    </div>
  );
};
